import { LitElement, TemplateResult, css, html, nothing } from 'lit';
import { consume } from '@lit/context';
import { inputResponseContext } from '../../contexts/inputResponse';
import { customElement, property, state } from 'lit/decorators.js';
import { BackendFormResponse, Producer } from '../../util/api';
import './components/plan-details-card';
import './components/mobile-details-nav';
import { makeCustomEvent, EVENT } from '../../util/events';
import { breakpointContext } from '../../contexts/breakpoint';
import { BREAKPOINT } from '../../util/breakpoints';
import { classMap } from 'lit/directives/class-map.js';
import {
    BackendPlan,
    getProviderLink,
    getPharmacyLink,
    getMedicationLink,
    PRODUCT_TYPE,
    METALLIC_LEVEL,
    PRICING,
    recommendPlanHandler,
    enrollPlanHandler,
    keepPlanHandler,
    selectNewPlanHandler,
    networkDescriptions2026,
    getPlanRate,
    getPlanName,
} from '../../util/plans';
import { Applicant, ApplicantRole } from './types/types';
import { PAGE } from '../../util/page';
import { sharedStyles } from '../../util/shared-styles';
import {
    productTypeTooltipMap,
    memberExperienceTooltip,
    medicalCareTooltip,
    overallStarRatingTooltip,
    planAdministrationTooltip,
    metalPlanTooltipMap,
} from '../../util/tooltips';
import { getAnalyticPlanDescription, getHostURL } from '../../util/analytics';
import { sourceContext } from '../../contexts/source';
import { deliveryMethodContext } from '../../contexts/deliveryMethod';
import { agentContext } from '../../contexts/agent';
import { SOURCE, sourcesToRecommend, isEmbeddedAgentEmailDelivery } from '../../util/source';
import { choose } from 'lit/directives/choose.js';
import { formatCurrency } from '../../util/format';
import { readOnlyContext } from '../../contexts/readOnly';
const bcbsncUrl = import.meta.env.VITE_BCBSNC_URL;

@customElement('rq-marketplace-details')
export class MarketPlaceDetails extends LitElement {
    @consume({ context: inputResponseContext, subscribe: true })
    @state()
    inputResponse!: BackendFormResponse;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @property({ type: String })
    subsidyAmount: string | undefined;

    @property({ type: Object })
    plan?: BackendPlan;

    @consume({ context: sourceContext })
    source!: SOURCE;

    @consume({ context: deliveryMethodContext })
    deliveryMethod?: string;

    @consume({ context: agentContext })
    agent?: Producer;

    @consume({ context: readOnlyContext, subscribe: true })
    @state()
    readOnly = false;

    @state()
    private activeSection: string = 'plan-overview';

    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.bcncDataLayer = window.bcncDataLayer || {};
        window.bcncDataLayer.page = window.bcncDataLayer.page || {};
        const baseUrl = `${getHostURL()}/details`;

        if (!this.plan) return;
        if (this.plan.producerRecommended) {
            window.bcncDataLayer.page.view_url = `${baseUrl}/recommended`;
        } else if (this.plan.pickOrder !== undefined && this.plan.pickOrder !== null) {
            window.bcncDataLayer.page.view_url = `${baseUrl}/${this.plan.pickOrder > 0 ? 'top' : 'all'}`;
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('scroll', this.handleScroll);
    }

    private handleScroll = () => {
        const sections = this.shadowRoot?.querySelectorAll('section');
        if (!sections) return;

        let currentSectionId = '';

        sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            if (rect.top >= 0 && rect.top <= window.innerHeight / 4) {
                currentSectionId = section.id;
            }
        });

        if (currentSectionId && this.activeSection !== currentSectionId) {
            this.activeSection = currentSectionId;
            this.requestUpdate();
        }
    };

    applicants: Applicant[] = [];
    static styles = [
        sharedStyles,
        css`
            .card {
                border-radius: 0.25rem;
                border: 1px solid var(--Color-neutral-200, #d1d3d4);
                background: var(--Themes-Light-White, #fff);
                box-shadow: 0px 8px 16px 0px rgba(0, 76, 108, 0.08);
                padding: 1.5rem;
                margin-top: 1.5rem;
            }
            .wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                gap: 2rem;
            }
            .content {
                display: flex;
                flex-direction: row;
                gap: 2rem;
                justify-content: center;
            }
            .stars {
                color: var(--lh-color-primary-500, #00aeef);
            }
            .pdfColor {
                color: var(--Primary-800, #006894);
            }
            .pdfLinks {
                font-family: 'Blue Ridge Sans';
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: 24px;
                text-decoration: none;
            }
            .sticky-buttons {
                position: sticky;
                top: 8.8rem;
                z-index: 10;
            }
            section {
                margin-bottom: 2rem;
            }
            .rates-table {
                border-collapse: collapse;
            }
            .rates-table td {
                font-size: 1rem;
            }
            .rates-table td:nth-child(2) {
                padding-left: 1.5rem;
            }
            .top-pick-chip {
                margin-left: auto;
            }
            .sub-header-wrapper {
                display: flex;
                justify-content: flex-start;
                gap: 0.8rem;
            }
            .sub-header {
                width: 70%;
            }
            .spaced-column {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            .button-group-container {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                padding: 1rem;
            }

            .button-group-container a {
                display: block;
                text-decoration: none;
                padding: 0.75rem 1rem;
                color: var(--Primary-800, #006894);
                font-family: 'Blue Ridge Sans';
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: 24px;
                border-radius: 4px;
                transition:
                    background-color 0.3s ease,
                    color 0.3s ease;
            }

            .button-group-container a.active {
                font-weight: bold;
                color: var(--lh-color-primary-700, #004c6c);
                background-color: var(--lh-color-neutral-100, #f0f0f0);
                border-left: 4px solid var(--lh-color-primary-500, #006894);
            }

            .button-group-container a:hover {
                background-color: var(--lh-color-neutral-200, #e0e0e0);
                color: var(--lh-color-primary-700, #004c6c);
            }

            .button-group-container lh-button {
                margin: 0;
            }
            .jump-to-label {
                color: var(--Neutral-600, #676d70);
                font-family: 'Blue Ridge Sans';
                font-size: 16px;
                font-style: normal;
                font-weight: 700;
                line-height: 16px;
                letter-spacing: 0.96px;
                text-transform: uppercase;
            }
            #plan-overview {
                margin-bottom: 0 !important;
                border-bottom: 1px solid var(--lh-color-neutral-300, #bfc3c4);
            }
            .plan-overview-wrapper {
                color: var(--Neutral-800, #222425);

                /* Text/Default/L */
                font-family: 'Blue Ridge Sans';
                font-size: 18px;
                font-style: normal;
                font-weight: 400;
                line-height: 24px; /* 133.333% */
            }
            hr {
                border: none;
                border-bottom: 1px solid var(--lh-color-neutral-300, #bfc3c4);
                margin: 1rem 0;
            }

            lh-accordion {
                border-bottom: 1px solid var(--lh-color-neutral-300, #bfc3c4);
            }
            .pt-12 {
                padding-top: 12px;
            }
            .pos-top-12 {
                position: relative;
                top: -20px;
            }
        `,
    ];

    get isPrimaryApplicant() {
        return this.applicants.some((applicant: { type: string }) => {
            return applicant.type === ApplicantRole.Primary;
        });
    }
    get isApplicanthaveSpouse() {
        return this.applicants.some((applicant: { type: string }) => {
            return applicant.type === ApplicantRole.Spouse;
        });
    }

    get isApplicantHaveChildren() {
        const child = this.applicants.some((applicant: { type: string }) => {
            return applicant.type === ApplicantRole.Child;
        });
        return child;
    }

    get getPricing(): TemplateResult | typeof nothing {
        if (!this.plan) return nothing;
        return this.plan.pricing === PRICING.EASY
            ? html`
                  <lh-accordion class="pos-top-12">
                      <lh-accordion-panel
                          header="Plan pricing"
                          .description=${html`<lh-icon name="tag"></lh-icon> Easy Pricing`}
                      >
                          <lh-typography type="text">
                              <p>Consider plans with easy pricing</p>
                              <p>Marketplace plans marked easy pricing:</p>
                              <ul>
                                  <li>
                                      Include some benefits before you reach the deductible. As soon as coverage starts,
                                      you’ll pay only a copayment for:
                                      <ul>
                                          <li>Doctor and specialist visits, including mental health</li>
                                          <li>Urgent care</li>
                                          <li>Physical, speech, and occupational therapy</li>
                                          <li>Generic and most preferred drugs</li>
                                      </ul>
                                  </li>
                                  <li>
                                      Are easier to compare because they have the same out-of-pocket costs within their
                                      health plan category, like:
                                      <ul>
                                          <li>Deductibles</li>
                                          <li>Out-of-pocket maximums</li>
                                          <li>Copayments/coinsurance</li>
                                      </ul>
                                  </li>
                              </ul>
                          </lh-typography>
                      </lh-accordion-panel>
                  </lh-accordion>
              `
            : html``;
    }

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    get isShopper() {
        return !this.agent || this.inputResponse?.context?.toLowerCase() === 'shopper';
    }

    private viewPlanResults() {
        this.dispatchEvent(makeCustomEvent(EVENT.CHANGE_PAGE, { page: PAGE.RESULTS }));
    }

    private renderRatesTable() {
        const planRate = getPlanRate(this.plan);
        return html`<table class="rates-table">
            <tbody>
                ${this.inputResponse.applicants.map((applicant) => {
                    if (!this.plan) return undefined;
                    const rate = (this.plan.rates || []).find(
                        (rate: { applicantIndex: string }) => rate.applicantIndex === applicant.index
                    );

                    return html`<tr>
                        <td>${this.getApplicantTypeLabel(applicant.type)}</td>
                        <td>+$${rate?.applicantRate.toFixed(2)}</td>
                    </tr>`;
                    return nothing;
                })}
                ${this.plan &&
                this.plan.marketplace === 'onExchange' &&
                this.inputResponse.subsidy?.subsidyEligible &&
                this.inputResponse.subsidy?.subsidyAmt
                    ? html`<tr>
                          <td>Subsidy</td>
                          <td>-$${this.inputResponse.subsidy.subsidyAmt}</td>
                      </tr>`
                    : nothing}
                <tr>
                    <td>Total</td>
                    <td>&nbsp;<strong>${formatCurrency(planRate)}</strong></td>
                </tr>
            </tbody>
        </table>`;
    }

    private getApplicantTypeLabel(type: string): string {
        switch (type) {
            case ApplicantRole.Primary:
                return `You${!this.isShopper ? 'r client' : ''}`;
            case ApplicantRole.Spouse:
                return `Your ${!this.isShopper ? "client's" : ''} Spouse/domestic partner`;
            case ApplicantRole.Child:
                return `Your ${!this.isShopper ? "client's" : ''} Child`;
            default:
                return '';
        }
    }

    private renderJumpToDropdown() {
        return html` <lh-container margin-top="md" margin-bottom="md" alignment="left" gutters="none">
            <lh-select id="jumpToDropdown" @lh-change=${this.handleJumpToChange}>
                <lh-option slot="options" value="">Jump to</lh-option>
                <lh-option slot="options" value="plan-overview">Plan overview</lh-option>
                <lh-option slot="options" value="doctor-visits">Doctor visits</lh-option>
                <lh-option slot="options" value="prescription-medications">Prescription medications</lh-option>
                <lh-option slot="options" value="labs-and-imaging-tests">Labs and imaging tests</lh-option>
                <lh-option slot="options" value="hospital-and-emergencies">Hospital and emergencies</lh-option>
                <lh-option slot="options" value="pregnancy-and-birth">Pregnancy and birth</lh-option>
            </lh-select></lh-container
        >`;
    }

    private handleJumpToChange(event: Event) {
        const target = event.target as HTMLSelectElement;
        if (target.id !== 'jumpToDropdown') {
            return;
        }

        const sectionId = target.value;
        if (sectionId) {
            const section = this.shadowRoot?.querySelector(`#${sectionId}`);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    get enrollButton(): TemplateResult | typeof nothing {
        if (!this.plan) return nothing;
        const handler = () => enrollPlanHandler(this.plan!.planId, this.plan!.qhpId, this.inputResponse);
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan!)}:enroll:button-filled`;
        return html`<lh-button
            analytics-id=${analyticTag}
            icon-position="left"
            icon="note-pencil"
            variant="filled"
            @click=${handler}
            .disabled=${this.readOnly}
            full-width
        >
            Enroll in this Plan
        </lh-button>`;
    }

    get recommendButton(): TemplateResult | typeof nothing {
        if (!this.plan) return nothing;
        const handler = () => {
            recommendPlanHandler(this.plan!.planId);
            this.viewPlanResults();
        };
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan!)}:recommend:button-filled`;
        return html`<lh-button
            icon-position="left"
            analytics-id=${analyticTag}
            icon="seal-check"
            variant="filled"
            @click=${handler}
            .disabled=${this.readOnly}
            full-width
        >
            Recommend Plan
        </lh-button>`;
    }

    get keepPlanButton(): TemplateResult | typeof nothing {
        if (!this.plan) return nothing;
        const handler = () => keepPlanHandler(this.plan!.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan!)}:keep:button-filled`;
        return html`<lh-button variant="filled" analytics-id=${analyticTag} @click=${handler} full-width
            >Keep Original Plan</lh-button
        >`;
    }

    get selectNewButton(): TemplateResult | typeof nothing {
        if (!this.plan) return nothing;
        const handler = () => selectNewPlanHandler(this.plan!.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan!)}:select-new:button-filled`;

        return html`<lh-button variant="filled" analytics-id=${analyticTag} @click=${handler} full-width
            >Select New Plan</lh-button
        >`;
    }

    get originalPlan() {
        return this.plan?.planCode === this.inputResponse.currentPlan;
    }

    get actionButton() {
        // Disable action button if plan is not selectable
        if (this.plan?.notSelectable) {
            return nothing;
        }
        // Always return a TemplateResult by using html`` `` as a fallback instead of nothing
        const safeButton = (button: TemplateResult | typeof nothing) => (button === nothing ? html`` : button);
        // When an embedded agent chooses to email the quote to their client, act like SOURCE.EMAIL
        // and always recommend (the embedded-agent quote request isn't flagged as producer context).
        if (isEmbeddedAgentEmailDelivery(this.source, this.deliveryMethod)) {
            return safeButton(this.recommendButton);
        }
        return choose(
            this.source,
            [
                ...sourcesToRecommend.map((s): [SOURCE, () => TemplateResult] => [
                    s,
                    () =>
                        this.inputResponse?.context?.toLowerCase() === 'shopper'
                            ? safeButton(this.enrollButton)
                            : safeButton(this.recommendButton),
                ]),
                [SOURCE.DIRECT_SHOPPER, () => safeButton(this.enrollButton)],
                [SOURCE.PABOL, () => safeButton(this.enrollButton)],
                [SOURCE.EMBEDDED_AGENT, () => safeButton(this.enrollButton)],
                [SOURCE.SALESFORCE, () => safeButton(this.enrollButton)],
                [
                    SOURCE.CHANGE_APP,
                    () => {
                        if (this.originalPlan) {
                            return safeButton(this.keepPlanButton);
                        }
                        return safeButton(this.selectNewButton);
                    },
                ],
            ],
            () => safeButton(this.enrollButton)
        );
    }

    private renderJumpToLinks() {
        return html`
            <div class="button-group-container">
                <lh-typogaphy variant="label" class="jump-to-label">JUMP TO</lh-typogaphy>
                <a
                    href="#plan-overview"
                    class="${classMap({ active: this.activeSection === 'plan-overview' })}"
                    @click=${(e: Event) => this.handleAnchorClick(e, 'plan-overview')}
                    @focus=${() => this.scrollToAnchor('plan-overview')}
                >
                    Plan overview
                </a>
                <a
                    href="#doctor-visits"
                    class="${classMap({ active: this.activeSection === 'doctor-visits' })}"
                    @click=${(e: Event) => this.handleAnchorClick(e, 'doctor-visits')}
                    @focus=${() => this.scrollToAnchor('doctor-visits')}
                >
                    Doctor visits
                </a>
                <a
                    href="#prescription-medications"
                    class="${classMap({
                        active: this.activeSection === 'prescription-medications',
                    })}"
                    @click=${(e: Event) => this.handleAnchorClick(e, 'prescription-medications')}
                    @focus=${() => this.scrollToAnchor('prescription-medications')}
                >
                    Prescription medications
                </a>
                <a
                    href="#labs-and-imaging-tests"
                    class="${classMap({
                        active: this.activeSection === 'labs-and-imaging-tests',
                    })}"
                    @click=${(e: Event) => this.handleAnchorClick(e, 'labs-and-imaging-tests')}
                    @focus=${() => this.scrollToAnchor('labs-and-imaging-tests')}
                >
                    Labs and imaging tests
                </a>
                <a
                    href="#hospital-and-emergencies"
                    class="${classMap({
                        active: this.activeSection === 'hospital-and-emergencies',
                    })}"
                    @click=${(e: Event) => this.handleAnchorClick(e, 'hospital-and-emergencies')}
                    @focus=${() => this.scrollToAnchor('hospital-and-emergencies')}
                >
                    Hospital and emergencies
                </a>
                <a
                    href="#pregnancy-and-birth"
                    class="${classMap({ active: this.activeSection === 'pregnancy-and-birth' })}"
                    @click=${(e: Event) => this.handleAnchorClick(e, 'pregnancy-and-birth')}
                    @focus=${() => this.scrollToAnchor('pregnancy-and-birth')}
                >
                    Pregnancy and birth
                </a>
                <lh-button-group orientation="vertical">
                    <lh-button
                        icon-position="left"
                        variant="outlined"
                        icon="caret-left"
                        full-width
                        @click=${() => this.viewPlanResults()}
                        analytics-id="rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(
                            this.plan!
                        )}:back:button-outlined"
                    >
                        Back to Plan Results
                    </lh-button>
                    ${this.actionButton}
                </lh-button-group>
            </div>
        `;
    }

    get topPickChip(): TemplateResult | typeof nothing {
        return nothing;
        // if (!this.plan || this.plan.pickOrder === undefined || this.plan.pickOrder < 0) return nothing;
        // return html`<div>
        //     <lh-chip variant="filled" color="primary">Top Pick</lh-chip>
        // </div>`;
    }
    private scrollToAnchor(sectionId: string) {
        const section = this.shadowRoot?.querySelector(`#${sectionId}`);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    private handleAnchorClick(event: Event, sectionId: string) {
        event.preventDefault();
        this.scrollToSection(sectionId);
    }

    private scrollToSection(sectionId: string) {
        const section = this.shadowRoot?.querySelector(`#${sectionId}`);
        if (section) {
            this.activeSection = sectionId;
            this.requestUpdate();
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    private formatValue(value: string | undefined, label: string): string {
        return value?.toLowerCase().includes('not covered') || value === 'N/A' ? 'Not Covered' : `${value} ${label}`;
    }

    private formatOutOfPocketDeductibleValue(value: string | undefined): string {
        return value === 'N/A' ? 'Not Covered' : value!;
    }

    render() {
        if (!this.plan) {
            return html`<p>Loading...</p>`;
        }

        const ratings = [
            { label: 'Overall rating', value: 'overallQualityRating', tooltip: overallStarRatingTooltip },
            { label: 'Member experience rating', value: 'memberExperienceRating', tooltip: memberExperienceTooltip },
            { label: 'Medical care rating', value: 'medicalCareRating', tooltip: medicalCareTooltip },
            {
                label: 'Plan administration rating',
                value: 'planAdministrationRating',
                tooltip: planAdministrationTooltip,
            },
        ];

        const doctorVisitsData = [
            {
                label: 'Primary care visit',
                inNetwork: this.plan.primaryCareInn,
                outOfNetwork: this.plan.primaryCareOon,
            },
            {
                label: 'Specialist visit',
                inNetwork: this.plan.specialistCareInn,
                outOfNetwork: this.plan.specialistCareOon,
            },
            {
                label: 'Preventive care, screening, and immunization',
                inNetwork: this.plan.preventiveCareInn,
                outOfNetwork: this.plan.preventiveCareOon,
            },
        ];

        const prescriptionMedicationsData = [
            { label: 'Tier 1 medication', inNetwork: this.plan.tier1MedInn, outOfNetwork: this.plan.tier1MedOnn },
            { label: 'Tier 2 medication', inNetwork: this.plan.tier2MedInn, outOfNetwork: this.plan.tier2MedOnn },
            { label: 'Tier 3 medication', inNetwork: this.plan.tier3MedInn, outOfNetwork: this.plan.tier3MedOnn },
            { label: 'Tier 4 medication', inNetwork: this.plan.tier4MedInn, outOfNetwork: this.plan.tier4MedOnn },
            { label: 'Tier 5 medication', inNetwork: this.plan.tier5MedInn, outOfNetwork: this.plan.tier5MedOnn },
        ];

        const labsAndImagingTestsData = [
            {
                label: 'X-rays and diagnostic tests',
                inNetwork: this.plan.xRaysandDInn,
                outOfNetwork: this.plan.xRaysandDOon,
            },
            { label: 'Imaging tests', inNetwork: this.plan.imagingTestsIn, outOfNetwork: this.plan.imagingTestsOon },
        ];

        const hospitalAndEmergenciesData = [
            { label: 'Urgent care', inNetwork: this.plan.urgentCareInn, outOfNetwork: this.plan.urgentCareOon },
            {
                label: 'Emergency room',
                inNetwork: this.plan.emergencyRoomGeneric,
                outOfNetwork: this.plan.emergencyRoomGeneric,
            },
            { label: 'Ambulance', inNetwork: this.plan.ambulanceInn, outOfNetwork: this.plan.ambulanceOOn },
            {
                label: 'Hospital stay (facility)',
                inNetwork: this.plan.hospitalStayFInn,
                outOfNetwork: this.plan.hospitalStayFOon,
            },
            {
                label: 'Hospital stay (physician)',
                inNetwork: this.plan.hospitalStayPInn,
                outOfNetwork: this.plan.hospitalStayPOon,
            },
            {
                label: 'Outpatient procedure (facility)',
                inNetwork: this.plan.outProFInn,
                outOfNetwork: this.plan.outProFOon,
            },
            {
                label: 'Outpatient procedure (doctor)',
                inNetwork: this.plan.outProDInn,
                outOfNetwork: this.plan.outProDOon,
            },
            {
                label: 'Physical therapy',
                inNetwork: this.plan.physicalTherapyInn,
                outOfNetwork: this.plan.physicalTherapyOon,
            },
        ];

        const pregnancyAndBirthData = [
            {
                label: 'Baby wellness care',
                inNetwork: this.plan.babyWellnessInn,
                outOfNetwork: this.plan.babyWellnessOon,
            },
            {
                label: 'Labor & delivery',
                inNetwork: this.plan.laborDeliverInn,
                outOfNetwork: this.plan.laborDeliverOon,
            },
        ];

        const networkDescription = networkDescriptions2026[this.plan.lobCode];

        // Determine the correct year for the info text
        const effectiveYear = this.inputResponse?.effectiveStart?.split('/')?.pop();
        const surveyYear = effectiveYear === '2026' ? '2025' : '2026';
        // Were asked to differentiate these plans by adding a "New Plan" label (May 2025)
        const newPlans = ['11512NC0430001', '11512NC0430002', '11512NC0430003'];
        const isNewPlan = newPlans.some((newPlan) => this.plan?.bolCode?.includes(newPlan));
        let isNewHMOPlan = false;
        if (this.plan.productType) {
            isNewHMOPlan = this.plan.productType?.includes(PRODUCT_TYPE.HMO);
        }
        return html`
            <lh-container
                theme="white"
                padding-top="sm"
                padding-bottom="sm"
                content-width="xl"
                gutters=${this.isMobile ? 'sm' : 'none'}
            >
                <lh-grid>
                    <!-- Left Sidebar -->
                    <lh-grid-item span="3">
                        <div class="sticky-buttons">${this.isMobile ? '' : this.renderJumpToLinks()}</div>
                    </lh-grid-item>

                    <!-- Main Content -->
                    <lh-grid-item span="9">
                        <lh-typography type="heading" selector=${this.isMobile ? 'h3' : 'h5'} size="xl"
                            >Plan details</lh-typography
                        >
                        ${this.isMobile ? this.renderJumpToDropdown() : ''}
                        <div class=${classMap({ card: !this.isMobile, '': this.isMobile })}>
                            <!-- Plan Overview  -->
                            <section id="plan-overview">
                                <lh-grid>
                                    <lh-grid-item span="9" span-tablet="8" span-mobile="12" vertical-align="top">
                                        <lh-typography type="text" size="xl"
                                            ><strong>${getPlanName(this.plan)}</strong></lh-typography
                                        > </lh-grid-item
                                    ><lh-grid-item span="3" span-tablet="4" span-mobile="12" vertical-align="top">
                                        <div
                                            class=${classMap({
                                                'top-pick-chip': true,
                                                'text-right': !this.isMobile,
                                                'text-left': this.isMobile,
                                            })}
                                        >
                                            ${this.topPickChip}
                                        </div>
                                    </lh-grid-item>
                                </lh-grid>
                                <lh-container padding-bottom="sm" padding-top="sm" spacing="none" gutters="none">
                                    <div class="align-items-end sub-header-wrapper">
                                        <div>
                                            <span style="color: var(--lh-color-primary-900)">
                                                <lh-icon name="hand-coins" size="xxl"></lh-icon>
                                            </span>
                                        </div>
                                        <div>
                                            <lh-typography type="heading" selector="h6" size="xl">
                                                Plan overview
                                            </lh-typography>
                                            <lh-typography type="text">
                                                Learn about the overall costs of this insurance plan.
                                            </lh-typography>
                                        </div>
                                    </div>
                                </lh-container>
                                <lh-accordion>
                                    <lh-accordion-panel
                                        header="Monthly premium"
                                        description="${formatCurrency(getPlanRate(this.plan))}"
                                    >
                                        ${this.renderRatesTable()}
                                    </lh-accordion-panel>

                                    <lh-accordion-panel header="Network type" description="${this.plan.header}">
                                        <div>
                                            <lh-typography selector="p">${this.plan.planNetwork}</lh-typography>
                                            ${networkDescription
                                                ? html`<lh-typography selector="p">
                                                      ${networkDescription}
                                                  </lh-typography>`
                                                : nothing}
                                        </div>
                                    </lh-accordion-panel>
                                </lh-accordion>
                                <div class="plan-overview-wrapper pt-12">
                                    <strong>Plan type</strong><br />
                                    <lh-tooltip
                                        .description=${productTypeTooltipMap.get(this.plan.productType as PRODUCT_TYPE)}
                                    >
                                        ${this.plan.productType}</lh-tooltip
                                    >
                                </div>
                                <hr />
                                ${this.getPricing}
                                <div class="plan-overview-wrapper">
                                    <strong>Coinsurance</strong><br />
                                    In-network: ${this.formatValue(this.plan.coinsuranceInn, 'coinsurance')}<br />
                                    Out-of-network: ${this.formatValue(this.plan.coinsuranceOon, 'coinsurance')}
                                </div>
                                <hr />
                                <div class="plan-overview-wrapper">
                                    <strong>Health savings account eligible</strong><br />
                                    ${this.plan.hsaEligible || 'No'}
                                </div>
                                <hr />
                                <div class="plan-overview-wrapper">
                                    <strong>Out-of-pocket maximums</strong><br />
                                    In-network:
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.outOfPocketMaxIndividualInn)}
                                    individual |
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.outOfPocketMaxFamilyMemberInn)}
                                    family member |
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.outOfPocketMaxFamilyInn)}
                                    family<br />
                                    Out-of-network:
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.outOfPocketMaxIndividualOon)}
                                    individual |
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.outOfPocketMaxFamilyMemberOon)}
                                    family member |
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.outOfPocketMaxFamilyOon)} family
                                </div>
                                <hr />
                                <div class="plan-overview-wrapper">
                                    <strong>Deductibles</strong><br />
                                    In-network:
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.individualDeductibleInn)}
                                    individual |
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.familyMemberDeductibleInn)} family
                                    member | ${this.formatOutOfPocketDeductibleValue(this.plan.familyDeductibleInn)}
                                    family<br />
                                    Out-of-network:
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.individualDeductibleOon)}
                                    individual |
                                    ${this.formatOutOfPocketDeductibleValue(this.plan.familyMemberDeductibleOon)} family
                                    member | ${this.formatOutOfPocketDeductibleValue(this.plan.familyDeductibleOon)}
                                    family
                                </div>
                                <hr />
                                <div class="plan-overview-wrapper">
                                    <strong>Prescription deductible </strong><br />
                                    ${this.plan.rxDeductible}
                                </div>
                                <hr />
                                <div class="plan-overview-wrapper">
                                    <strong>Metal level</strong><br />
                                    <lh-tooltip
                                        .description=${metalPlanTooltipMap.get(this.plan.metal as METALLIC_LEVEL)}
                                        >${this.plan.metal}</lh-tooltip
                                    >
                                </div>
                                <hr />
                                <div class="plan-overview-wrapper">
                                    <strong>Ratings</strong><br />

                                    ${ratings.map(
                                        (rating) => html`
                                            <div>
                                                <lh-typography selector="span">
                                                    <lh-tooltip description=${rating.tooltip}>
                                                        ${rating.label}:
                                                    </lh-tooltip>
                                                </lh-typography>
                                                <span>
                                                    ${this.plan &&
                                                    Number(this.plan[rating.value as keyof typeof this.plan]) === 0
                                                        ? html`<span class="not-rated"
                                                              >${isNewPlan || isNewHMOPlan ? 'New Plan - ' : ''}Not
                                                              Rated</span
                                                          >`
                                                        : Array.from(
                                                              { length: 5 },
                                                              (_, i) => html`
                                                                  <span class="stars">
                                                                      <lh-icon
                                                                          size="xs"
                                                                          name="${Number(
                                                                              this.plan &&
                                                                                  this.plan[
                                                                                      rating.value as keyof typeof this.plan
                                                                                  ]
                                                                          ) > i
                                                                              ? 'star-fill'
                                                                              : 'star'}"
                                                                      ></lh-icon>
                                                                  </span>
                                                              `
                                                          )}
                                                </span>
                                            </div>
                                        `
                                    )}
                                </div>
                                <hr />

                                <!-- Official Documents -->
                                <lh-grid style="margin-bottom: 0.9375rem">
                                    <lh-grid-item>
                                        <lh-typography><strong>Official documents</strong></lh-typography>
                                    </lh-grid-item>
                                    <lh-grid-item>
                                        <lh-typography>
                                            <a
                                                class="pdfLinks pdfColor"
                                                href="${bcbsncUrl}/assets/bol/public/pdf/sbc/${this.plan.pdfName}.pdf"
                                                target="_blank"
                                            >
                                                Summary of benefits and coverages
                                            </a>
                                            <lh-icon name="file" class="pdfColor"></lh-icon>
                                        </lh-typography>
                                        <lh-typography>
                                            <a
                                                class="pdfLinks pdfColor"
                                                href="${getMedicationLink(
                                                    this.plan.pricing,
                                                    this.inputResponse?.effectiveStart
                                                )}"
                                                target="_blank"
                                            >
                                                Formulary (list of covered medications)
                                            </a>
                                            <lh-icon name="arrow-square-out" class="pdfColor"></lh-icon>
                                        </lh-typography>

                                        <lh-typography>
                                            <a
                                                class="pdfLinks pdfColor"
                                                href="${getProviderLink(this.plan.lobCode)}"
                                                target="_blank"
                                                >Provider list</a
                                            >
                                            <lh-icon name="arrow-square-out" class="pdfColor"></lh-icon>
                                        </lh-typography>
                                        <lh-typography>
                                            <a
                                                class="pdfLinks pdfColor"
                                                href="${getPharmacyLink(
                                                    this.plan?.heatmapNetwork,
                                                    this.inputResponse?.effectiveStart
                                                )}"
                                                target="_blank"
                                            >
                                                Pharmacy list
                                            </a>
                                            <lh-icon name="arrow-square-out" class="pdfColor"></lh-icon>
                                        </lh-typography>
                                    </lh-grid-item>
                                </lh-grid>
                            </section>

                            <!-- Doctor Visits Section -->
                            <section id="doctor-visits">
                                <rq-plan-details-card
                                    title="Doctor visits"
                                    icon="doctor"
                                    .data=${doctorVisitsData}
                                ></rq-plan-details-card>
                            </section>

                            <!-- Prescription Medications Section -->
                            <section id="prescription-medications">
                                <rq-plan-details-card
                                    title="Prescription medications"
                                    icon="pill"
                                    description="The amount you’ll pay will depend on your plan coverage, if you’ve met a deductible, and the medication type. For more information, check the formulary in the plan’s official documents."
                                    .data=${prescriptionMedicationsData}
                                ></rq-plan-details-card>
                            </section>

                            <!-- Labs and Imaging Tests Section -->
                            <section id="labs-and-imaging-tests">
                                <rq-plan-details-card
                                    title="Labs and imaging tests"
                                    icon="test-tube"
                                    description="These services can help providers diagnose conditions. Imaging tests include services like CT scans, PET scans, or MRI scans."
                                    .data=${labsAndImagingTestsData}
                                ></rq-plan-details-card>
                            </section>

                            <!-- Hospital and Emergencies Section -->
                            <section id="hospital-and-emergencies">
                                <rq-plan-details-card
                                    title="Hospital and emergencies"
                                    icon="hospital"
                                    .data=${hospitalAndEmergenciesData}
                                ></rq-plan-details-card>
                            </section>

                            <!-- Pregnancy and Birth Section -->
                            <section id="pregnancy-and-birth">
                                <rq-plan-details-card
                                    title="Pregnancy and birth"
                                    icon="baby"
                                    .data=${pregnancyAndBirthData}
                                ></rq-plan-details-card>
                            </section>
                        </div>

                        <!-- Plan Details Info Text -->
                        <lh-container padding-top="md" content-width="xxl" alignment="left" gutters="sm">
                            <lh-typography size="sm">
                                Plan quality ratings and enrollee survey results are calculated by CMS using data
                                provided by health plans in ${surveyYear}. The ratings are being displayed for plans for
                                the ${effectiveYear} plan year. Learn more about these ratings.
                                <a href="https://www.healthcare.gov/quality-ratings/" target="_blank"
                                    >https://www.healthcare.gov/quality-ratings/<lh-icon
                                        size="sm"
                                        name="arrow-square-out"
                                        class="pdfColor"
                                    ></lh-icon
                                ></a>
                            </lh-typography>
                        </lh-container>
                    </lh-grid-item>
                </lh-grid>
            </lh-container>
            ${this.isMobile
                ? html`<rq-mobile-details-nav
                      .plan=${this.plan}
                      .onBackToPlanResults=${() => this.viewPlanResults()}
                  ></rq-mobile-details-nav>`
                : nothing}
        `;
    }
}
declare global {
    interface HTMLElementTagNameMap {
        'rq-marketplace-details': MarketPlaceDetails;
    }
}
